import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

import type { CampaignContactError } from '@repo/shared-types';
import type { AxiosError } from 'axios';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type ValidationError = {
  field: string;
  message: string;
};

/**
 * Extracts validation errors from axios error response.
 * The backend ZodValidationPipe formats errors as:
 * { message: 'Validation failed', errors: [{ field: string, message: string }] }
 */
export function extractValidationErrors(error: unknown): ValidationError[] {
  if (!error || typeof error !== 'object') {
    return [];
  }

  const axiosError = error as AxiosError<{ message?: string; errors?: ValidationError[] }>;

  if (axiosError.response?.data?.errors && Array.isArray(axiosError.response.data.errors)) {
    return axiosError.response.data.errors;
  }

  return [];
}

/**
 * Extracts per-contact errors from a failed campaign create. The backend
 * returns `{ message, contact_errors: [{ position, message, ... }] }` when
 * specific uploaded contacts could not be saved.
 */
export function extractContactErrors(error: unknown): CampaignContactError[] {
  if (!error || typeof error !== 'object') {
    return [];
  }

  const axiosError = error as AxiosError<{ contact_errors?: CampaignContactError[] }>;
  const contactErrors = axiosError.response?.data?.contact_errors;
  if (!Array.isArray(contactErrors)) {
    return [];
  }

  return contactErrors.filter((e) => typeof e?.message === 'string' && e.message.length > 0);
}

/**
 * Extracts the error message from an axios error response.
 * Returns the message from the response body, or a default error message.
 */
export function extractErrorMessage(error: unknown, defaultMessage = 'An error occurred'): string {
  if (!error || typeof error !== 'object') {
    return defaultMessage;
  }

  const axiosError = error as AxiosError<{ message?: string }>;

  if (axiosError.response?.data?.message) {
    return axiosError.response.data.message;
  }

  if (axiosError.message) {
    return axiosError.message;
  }

  return defaultMessage;
}
